import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  MenuItem,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Group_Parameter.css";
import apiService from "../../utils/apiservice";

const API_URL = process.env.REACT_APP_API_DJANGO_URL;

const GroupParameter = () => {
  const [devices, setDevices] = useState([]);
  const [existingGroups, setExistingGroups] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [availableParams, setAvailableParams] = useState([]); // Parameters not in group
  const [assignedParams, setAssignedParams] = useState([]); // Parameters in group

  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [tempGroupName, setTempGroupName] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [groupError, setGroupError] = useState("");
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);


  // const groupNameRegex = /^[A-Za-z0-9_-]+( [A-Za-z0-9_-]+)*$/;

  const groupNameRegex =
    /^(?!.*[-_]{2,})(?!.*\s{2,})[A-Za-z0-9]+([ _-]?[A-Za-z0-9]+)*$/;

  const validateGroupName = (value) => {
    if (!value) return "Group name is required";

    if (value.length < 4 || value.length > 20) {
      return "Must be 4–20 characters";
    }

    if (!groupNameRegex.test(value)) {
      return "Only letters, numbers, _ , - and single spaces allowed";
    }

    // ❌ Only numbers not allowed
    if (/^[0-9]+$/.test(value)) {
      return "Only numbers are not allowed";
    }

    return "";
  };
  useEffect(() => {
    if (!openModal) {
      setGroupError("");       // ✅ clear error
      setTempGroupName("");    // ✅ optional: reset input
    }
  }, [openModal]);

  // Reset page when group or tab changes
  useEffect(() => {
    setPage(0);
  }, [selectedGroupId, tabValue]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };




  const getConfig = useCallback(
    () => ({
      headers: {
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      data: {},
    }),
    [],
  );


  const fetchCustomers = async () => {
    try {
      const res = await apiService({
        url: `/api/get_customers/`,
        method: "GET",
      });

      console.log("Customer API Response:", res.data);

      // API directly returns array
      const customerData = Array.isArray(res.data) ? res.data : [];

      setCustomers(customerData);

      // auto-select first customer
      if (customerData.length > 0) {
        const firstCustomerId = customerData[0].tb_customer_id;

        setSelectedCustomer(firstCustomerId);

        // fetch related devices
        fetchInitialData(firstCustomerId);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch customers");
    }
  };

  const fetchInitialData = useCallback(async (customerId) => {
    try {
      // const customerId = localStorage.getItem("customer_id");

      // const [devRes, groupRes] = await Promise.all([
      //   axios.get(
      //     `${API_URL}/api/customer_devices/${customerId}/?page=0&pageSize=10`,
      //     getConfig(),
      //   ),
      //   axios.get(`${API_URL}/api/get_all_groups_names/`, getConfig()),
      // ]);


      const [devRes, groupRes] = await Promise.all([
        apiService({
          url: `/api/customer_devices/${customerId}/?page=0&pageSize=1000`,
          method: "GET",
          data: {},
        }),
        apiService({
          url: `/api/get_all_groups_names/`,
          method: "GET",
        }),
      ]);
      // ✅ DEFINE devData FIRST
      const devData = devRes.data.data.data.map((d) => ({
        id: d.id.id,
        name: d.name,
      }));

      // ✅ FIX GROUP FORMAT ALSO
      const formattedGroups = (groupRes.data.groups || []).map((g) => ({
        id: g.group_id,
        group_name: g.group_name,
        device_id: g.device_id,
      }));

      setDevices(devData);
      setExistingGroups(formattedGroups);

      if (devData.length > 0) {
        setSelectedDevice(devData[0].id);
      }

    } catch (err) {
      toast.error("Failed to fetch initial data");
    }
  }, [getConfig]);

  // Function to open the delete confirmation modal
  const triggerDeleteModal = () => {
    setOpenDeleteModal(true);
  };

  // Function to perform the actual delete API call
  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(
        `${API_URL}/api/delete_group/${selectedGroupId}/`,
        getConfig(),
      );

      toast.success("Group deleted successfully"); // Toast for success

      // Reset UI
      setOpenDeleteModal(false);
      setSelectedGroupId("");
      setAssignedParams([]);
      setAvailableParams([]);
      // fetchInitialData();
      await fetchInitialData(selectedCustomer);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete group");
    } finally {
      setLoading(false);
    }
  };

  const fetchParameters = useCallback(
    async (deviceId, groupId) => {
      if (!deviceId || !groupId) return;
      try {
        setLoading(true);

        // 1. Fetch all parameters available for the device
        // 2. Fetch the specific groups and their assigned parameters
        // const [deviceParamsRes, groupWiseRes] = await Promise.all([
        //   axios.get(
        //     `${API_URL}/api/get_device_parameters/${deviceId}/`,
        //     getConfig(),
        //   ),
        //   axios.get(
        //     `${API_URL}/api/get_group_parameters/${deviceId}/`,
        //     getConfig(),
        //   ),
        // ]);
        const [deviceParamsRes, groupWiseRes] = await Promise.all([
          apiService({
            url: `/api/get_device_parameters/${deviceId}/`,
            method: "GET",
            data: {},
          }),
          apiService({
            url: `/api/get_group_parameters/${deviceId}/`,
            method: "GET",
            data: {},
          }),
        ]);

        const allParams = deviceParamsRes.data.parameters || [];
        const groupData = groupWiseRes.data.groups || [];

        // 3. Find the currently selected group in the group-wise API response
        const currentGroup = groupData.find(
          (g) => g.group_id === groupId
        );

        const assignedList = currentGroup ? currentGroup.parameters : [];
        const assignedIds = assignedList.map((p) => p.parameter_id);

        // 4. Map 'is_assigned_to_group' property for the UI toggle
        const formattedAssigned = assignedList.map((p) => ({
          ...p,
          is_assigned_to_group: true,
        }));
        const formattedAvailable = allParams
          .filter((p) => !assignedIds.includes(p.parameter_id))
          .map((p) => ({ ...p, is_assigned_to_group: false }));

        setAssignedParams(formattedAssigned);
        setAvailableParams(formattedAvailable);
      } catch (err) {
        console.error("Error fetching group parameters:", err);
        toast.error("Failed to load parameters");
      } finally {
        setLoading(false);
      }
    },
    [getConfig, existingGroups],
  );

  // Update effect to include the necessary dependencies
  useEffect(() => {
    if (selectedDevice && selectedGroupId && existingGroups.length > 0) {
      fetchParameters(selectedDevice, selectedGroupId);
    }
  }, [selectedDevice, selectedGroupId, existingGroups, fetchParameters]);

  const handleAction = async (param, action) => {
    const isAssigning = action === "assign";
    const endpoint = isAssigning
      ? "assign_group_parameters"
      : "unassign_group_parameters";
    try {

      await axios.post(
        `${API_URL}/api/${endpoint}/${selectedDevice}/${selectedGroupId}/`,
        { parameter_ids: [param.parameter_id] },
        getConfig(),
      );

      toast.success(`${isAssigning ? "Assigned" : "Unassigned"} successfully`);
      // Refresh both lists
      fetchParameters(selectedDevice, selectedGroupId);
    } catch (err) {
      toast.error(err.response?.data?.error || "Action failed");
    }
  };

  // ... rest of your component

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleSaveGroup = async () => {
    const error = validateGroupName(tempGroupName);

    if (error) {
      toast.error(error);
      return;
    }

    try {
      setLoading(true);

      if (modalMode === "create") {
        await axios.post(
          `${API_URL}/api/create_group/`,
          { group_name: tempGroupName },
          getConfig()
        );
        toast.success("Group created successfully!");
      } else {
        await axios.post(
          `${API_URL}/api/update_group_name/${selectedGroupId}/`,
          { group_name: tempGroupName },
          getConfig()
        );
        toast.info("Group Updated Successfully!");
      }

      setOpenModal(false);
      setTempGroupName("");
      fetchInitialData(selectedCustomer);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save group");
    } finally {
      setLoading(false);
    }
  };

  const getFilteredData = () => {
    if (tabValue === 1) return assignedParams;
    if (tabValue === 2) return availableParams;
    return [...assignedParams, ...availableParams];
  };

  // Add this helper function inside your component
  const handleOpenModal = (mode) => {
    setModalMode(mode);
    if (mode === "edit") {
      const group = existingGroups.find((g) => g.id === selectedGroupId);
      setTempGroupName(group ? group.group_name : "");
    } else {
      setTempGroupName("");
    }
    setOpenModal(true);
  };

  // Then update your buttons to call it:
  // For Create: onClick={() => handleOpenModal("create")}
  // For Edit:   onClick={() => handleOpenModal("edit")}

  return (
    <Box>
      <ToastContainer position="top-right" autoClose={2000} />

      <Paper className="main-card-ui" elevation={0}>
        {/* TOP ROW: Dropdowns and Create */}
        <Box className="header-controls">
          <TextField
            select
            label="Select Customer"
            value={selectedCustomer}
            onChange={(e) => {
              const customerId = e.target.value;
              setSelectedCustomer(customerId);
              fetchInitialData(customerId);
            }}
            size="small"
            sx={{ width: 220 }}
          >
            {customers.map((cust) => (
              <MenuItem
                key={cust.tb_customer_id}
                value={cust.tb_customer_id}
              >
                {cust.customer_name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Select Device"
            value={selectedDevice}
            onChange={(e) => setSelectedDevice(e.target.value)}
            size="small"
            sx={{ width: 220 }}
          >
            {devices.map((dev) => (
              <MenuItem key={dev.id} value={dev.id}>
                {dev.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Select Existing Group"
            value={selectedGroupId}
            onChange={(e) => setSelectedGroupId(e.target.value)}
            size="small"
            sx={{ width: 220 }}
          >
            {existingGroups.map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.group_name}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="contained"
            className="create-group-btn"
            startIcon={<CreateNewFolderIcon />}
            onClick={() => {
              setModalMode("create");
              setTempGroupName("");
              setOpenModal(true);
            }}
          >
            Create New Group
          </Button>
        </Box>

        {/* GROUP ACTION BAR */}
        {selectedGroupId && (
          <Box className="group-info-bar">
            <Typography variant="body2">
              Current Group:{" "}
              <strong>
                {
                  existingGroups.find((g) => g.id === selectedGroupId)
                    ?.group_name
                }
              </strong>
            </Typography>
            <Box className="badge-row">
              <div className="status-badge assigned">
                Assigned: {assignedParams.length}
              </div>
              <div className="status-badge available">
                Available: {availableParams.length}
              </div>
              {/* <IconButton size="small" onClick={() => { setModalMode("edit"); setOpenModal(true); }}><EditIcon color="primary" fontSize="small"/></IconButton> */}

              {/* Change the Edit IconButton to this: */}
              <IconButton
                size="small"
                onClick={() => {
                  setModalMode("edit");
                  // ✅ Find the group name from the list and set it to tempGroupName
                  const currentGroup = existingGroups.find(
                    (g) => g.id === selectedGroupId,
                  );
                  setTempGroupName(currentGroup ? currentGroup.group_name : "");
                  setOpenModal(true);
                }}
              >
                <EditIcon color="primary" fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={triggerDeleteModal} // ✅ Opens the Dialog instead of alert
              >
                <DeleteIcon color="error" fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}

        {/* TABS (TOGGLE UI) */}
        <Box className="tabs-wrapper">
          <Tabs
            value={tabValue}
            onChange={(e, v) => setTabValue(v)}
            centered
            className="custom-tabs"
            TabIndicatorProps={{ style: { display: "none" } }}
          >
            <Tab label="All" />
            <Tab label="Assigned Group Parameters" />
            <Tab label="Available Parameters" />
          </Tabs>
        </Box>

        {/* TABLE */}
        <TableContainer className="table-container-ui orange-table">
          {loading ? (
            <Box textAlign="center" p={5}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <Table stickyHeader size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Parameter Name</TableCell>
                  <TableCell align="center">Key Name</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredData().slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow key={row.parameter_id}>
                      <TableCell align="center">{row.display_name}</TableCell>
                      <TableCell align="center" className="code-text">{row.key_name}</TableCell>
                      <TableCell align="center">
                        {row.is_assigned_to_group ? (
                          <Button
                            className="btn-unmap"
                            onClick={() => handleAction(row, "unassign")}
                          >
                            Unassign
                          </Button>
                        ) : (
                          <Button
                            className="btn-map"
                            onClick={() => handleAction(row, "assign")}
                          >
                            Assign
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}

          {/* Add this after </TableContainer> */}
          {!loading && getFilteredData().length > 0 && (
            <div className="pagination-bar">
              <div className="rows-select">
                Rows per page:
                <select value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                </select>
              </div>
              <div className="page-info">
                {page * rowsPerPage + 1}-
                {Math.min((page + 1) * rowsPerPage, getFilteredData().length)} of {getFilteredData().length}
              </div>
              <div className="page-arrows">
                <button disabled={page === 0} onClick={() => setPage(page - 1)}>
                  ‹
                </button>
                <button
                  disabled={(page + 1) * rowsPerPage >= getFilteredData().length}
                  onClick={() => setPage(page + 1)}
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </TableContainer>
      </Paper>

      {/* Modal for Group Create/Rename */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {modalMode === "create" ? "Create Group" : "Update Group Name"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Group Name"
            fullWidth
            variant="standard"
            value={tempGroupName}
            onChange={(e) => {
              let value = e.target.value;

              // ✅ Fix spaces
              value = value
                .replace(/\s+/g, " ")   // multiple → single
                .replace(/^\s/, "");    // no starting space

              setTempGroupName(value);
              setGroupError(validateGroupName(value)); // ✅ live validation
            }}
            error={!!groupError}
            helperText={groupError}

          // onChange={(e) => setTempGroupName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button onClick={handleSaveGroup} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE CONFIRMATION MODAL */}
      <Dialog
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ color: "#d32f2f", fontWeight: "bold" }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure to delete this Group ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpenDeleteModal(false)}
            color="inherit"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Delete Group"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupParameter;